import { useEffect, useRef, type FC } from "react";
import { useSearchParams } from "react-router-dom";

import { useRequestsIde } from "../context/RequestsIdeContext";
import { useRequestsData } from "../data/RequestsDataContext";
import {
  hasLegacyIntakeParams,
  migrateLegacyIntakeParams,
  NEW_SEARCH_PARAM,
  parseNewParam,
  parseOpenParam,
  REQUESTS_TAB
} from "../routes";
import type { NewRequestAction } from "../types";
import { submissionArtifactId } from "../types";

type Props = {
  /** Start a new request the way a deep link asked (`?new=`). */
  onNewRequest: (action: NewRequestAction) => void;
};

/**
 * The deep links `?open=` can't serve by itself being the page:
 * - legacy `oi_*` links are rewritten first, so every old link (activity
 *   feed, dashboard, emails, agent refs) still lands;
 * - `?new=…` starts its action once the data is in, then is dropped so a
 *   refresh does not repeat it;
 * - `open=template:` sends the template to Firm Knowledge, where templates live;
 * - a link that lands on an intake awaiting review opens its latest
 *   submission instead (only on arrival, so the intake page stays reachable).
 */
export const OpenFromUrl: FC<Props> = ({ onNewRequest }) => {
  const [params, setParams] = useSearchParams();
  const { open } = useRequestsIde();
  const data = useRequestsData();
  const search = params.toString() ? `?${params}` : "";
  const handledNew = useRef<string | null>(null);
  const arrived = useRef(false);

  useEffect(() => {
    if (!hasLegacyIntakeParams(search)) return;
    setParams(new URLSearchParams(migrateLegacyIntakeParams(search)), {
      replace: true
    });
  }, [search, setParams]);

  const legacy = hasLegacyIntakeParams(search);
  const onRequests = params.get("tab") === REQUESTS_TAB;
  const request = legacy || !onRequests ? null : parseOpenParam(search);
  const action = legacy ? null : parseNewParam(search);
  const ready = !data.isLoading;

  useEffect(() => {
    if (request?.kind !== "template") return;
    open({ kind: "template", id: request.id, title: "" }, { replace: true });
  }, [request?.kind, request?.id, open]);

  useEffect(() => {
    if (!ready || arrived.current) return undefined;
    arrived.current = true;
    if (request?.kind !== "intake") return undefined;
    const intake = data.intakes.find(
      (i) => i.outgoing_intake_id === request.id
    );
    if (intake?.status !== "awaiting_review") return undefined;
    let cancelled = false;
    void data.loadDetail(intake.outgoing_intake_id).then((detail) => {
      const latest = detail?.submissions
        .slice()
        .sort((a, b) => b.revision_number - a.revision_number)[0];
      if (cancelled || !latest) return;
      open(
        {
          kind: "submission",
          id: submissionArtifactId(
            intake.outgoing_intake_id,
            latest.outgoing_intake_submission_id
          ),
          title: `${intake.title} · Submission #${latest.revision_number}`
        },
        { replace: true }
      );
    });
    return () => {
      cancelled = true;
    };
    // Once, on the first render with data.
  }, [ready]);

  useEffect(() => {
    if (!action || !ready) return;
    const token = JSON.stringify(action);
    if (handledNew.current === token) return;
    handledNew.current = token;
    onNewRequest(action);
    const next = new URLSearchParams(params);
    next.delete(NEW_SEARCH_PARAM);
    setParams(next, { replace: true });
  }, [action ? JSON.stringify(action) : null, ready]);

  return null;
};
